/**
 * "Sign in with Ledger" OAuth 2.0 client — RFC 6749 §4.1 (Authorization
 * Code) + RFC 7636 (PKCE) — for the WagePro payroll web app.
 *
 * wagepro-web is a static SPA with no backend of its own, so it holds the
 * access/refresh token pair client-side (via `token_store.ts`) and calls
 * `apps/api` as an audience-gated Bearer token, never a business session
 * cookie (docs/architecture-boundaries.md's OAuth-scoped-grant invariant).
 *
 * This pass requests `scope=identity payroll:employer:read
 * payroll:employer:write payroll:self:read` and an RFC 8707 `resource` param
 * (this app's own origin) on the authorize request, so the minted token
 * carries a non-null audience bound to `env.WAGEPRO_OAUTH_AUDIENCE` (#21929
 * PR3) and can actually reach the `payroll:employer:*`-gated procedures —
 * identity-only tokens from before this pass could not.
 *
 * `payroll:self:read` (C7 PR B, #21938) was added alongside `identity` and
 * `payroll:employer:*` for five reasons:
 *
 *  1. wagepro-web is ONE OAuth-bearer app serving TWO surfaces — the
 *     employer admin console and the employee-facing "My Pay" ESS view
 *     (`App.tsx` branches on `!is_admin` to pick between them). A single
 *     app, a single token, a single scope request.
 *  2. This composes with C7 PR A (#21938, merged): `AUDIENCE_SCOPE_FAMILIES
 *     .wagepro` (`apps/api/src/trpc/oauth_allowlist.ts`) now carries
 *     `payroll:self:read` alongside the two `payroll:employer:*` scopes, so a
 *     wagepro-audience token that also holds this scope can actually reach
 *     `personalPayroll.listEmployerLinks` / `listPayslips`.
 *  3. `payroll:self:read` is NOT in `ENTITY_SCOPED_SCOPES`
 *     (`apps/api/src/lib/entity_scoped_scopes.ts`) — adding it changes
 *     nothing about the entity picker or the `oauth_capabilities` grant
 *     record; that machinery is driven entirely by `payroll:employer:*`.
 *  4. It is already inside this client's registered scope ceiling —
 *     `20260729000001_wagepro_employer_oauth_scopes.sql` sets
 *     `first_party_wagepro_web`'s `oauth_clients.scopes` to `'identity
 *     payroll:self:read payroll:employer:read payroll:employer:write'` — so
 *     requesting it here cannot produce `invalid_scope` at `GET
 *     /oauth/authorize` (`apps/api/src/routes/oauth.ts` ~line 636 validates
 *     the requested scope set against exactly this column). See
 *     `oauth_pkce.test.ts`'s registered-ceiling regression test.
 *  5. It widens the scope set beyond whatever a user may have already
 *     consented to under the prior 3-scope request, so a returning user sees
 *     the real consent screen once more — transparent to `completeSignIn`
 *     below, which only ever sees the final `?code=&state=` callback either
 *     way; no frontend change was needed to support it.
 *
 * WIDENED BLAST RADIUS (accepted, named explicitly per adversarial review —
 * PR #22237): because this is one token for one app, EVERY wagepro-web
 * session now carries `payroll:self:read`, including an employer-admin
 * user who never opens "My Pay". Token theft or an XSS in the admin console
 * therefore additionally exposes that user's OWN personal payslip
 * snapshots (never anyone else's — `listPayslips`/`getPayslip` still
 * independently re-validate an active `employer_links` row per caller), not
 * just employer-facing data. This is a deliberate one-app/one-token
 * trade-off (reason 1 above), not an oversight.
 *
 * `first_party_wage_mobile` is unaffected by any of this — it keeps `identity
 * payroll:self:read` only (that pairing is simply mobile's entire scope set,
 * not an exclusive claim on `payroll:self:read` — this app now requests the
 * same scope for its own ESS surface, alongside its employer-facing scopes).
 *
 * `first_party_wagepro_web` is a first-party client
 * (20260720000000_wagepro_oauth_client.sql), but `apps/api/src/routes/oauth.ts`
 * `canFirstPartyAutoApprove`'s silent-consent bypass is scoped to
 * IDENTITY-ONLY requests (`IDENTITY_ONLY_SCOPES = {'identity'}`) — it does
 * NOT cover `payroll:employer:*`. So unlike the pre-PR4 identity-only flow,
 * a user who hasn't already consented (or whose prior consent doesn't cover
 * this exact scope set) IS redirected to Ledger's `/oauth/consent` UI
 * (`apps/ledger-web`, with the entity picker `entity_selection_required=1`
 * — #21929's earlier PRs) before landing back on `redirect_uri` with a code.
 * That consent round-trip is transparent to `completeSignIn` below — it only
 * ever sees the final `?code=&state=` callback, identical in shape to the
 * silent-approve case — so no frontend change was needed to support it, only
 * this doc correction (an earlier draft incorrectly claimed no consent
 * screen would ever appear).
 *
 * `resource` is sent ONLY on the authorize request, never on the token
 * exchange or refresh grant — the API resolves `audience`/`auth_strength`
 * from the stored authorization-code/token row at those steps, not from a
 * fresh caller-supplied value, so repeating it there would be a no-op at
 * best.
 *
 * `apps/wagepro-mobile` DOES run this module now — it is a thin Capacitor
 * shell serving this app's own `dist/`, so there is exactly one
 * implementation of sign-in across web and mobile (CLAUDE.md § "Mobile App
 * Architecture"). The shell's served origin is `http://localhost` (no port),
 * which matches no configured audience slot, so `resource` is resolved
 * separately from `redirect_uri` via `lib/api_target.ts`'s
 * `resolveOAuthResource` rather than both being `window.location.origin`:
 * `redirect_uri` stays the real WebView origin (registered for
 * `first_party_wagepro_web` — locally by `apps/api/seed.sql`), while
 * `resource` carries the app's canonical origin so the minted token lands in
 * WagePro's audience slot. The shell therefore reuses the same OAuth client,
 * the same four scopes, and the same audience↔scope family as the web app —
 * no separate mobile client, no narrowed surface. It does NOT use
 * `first_party_wage_mobile`; that client is the standalone ESS companion and
 * carries only `identity payroll:self:read`, which would break this app's
 * employer console.
 *
 * KNOWN RISK (accepted, deferred — #22019, not fixed by this pass): the
 * `/oauth/authorize` endpoint stores whatever `resource` value the caller
 * sends without validating it against a configured audience allowlist at
 * that step. No deployment of wagepro-web exists today, but if a developer
 * ever points a local wagepro-web build at a *deployed* `apps/api` (via
 * `VITE_API_URL`) in an environment where `WAGEPRO_OAUTH_AUDIENCE` is
 * unconfigured, sign-in itself will still SUCCEED — the authorize/token
 * round-trip has no reason to fail — but the minted token's audience won't
 * match anything the API recognizes, so every subsequent tRPC call
 * (including `auth.identity`, the very call `AuthGate` uses to confirm
 * sign-in worked) comes back FORBIDDEN. That failure clears stored tokens,
 * which flips `AuthGate`'s boot status to `unauthenticated`, which
 * auto-triggers `beginSignIn()` again — and because this isn't an
 * IdP-redirect `error` param (see `getOAuthCallbackError` above), the
 * `error` boot status never fires, so the user sees a silent infinite
 * redirect loop instead of a visible error. Fixing this properly needs a
 * boot-loop circuit breaker that can distinguish "token was just cleared
 * immediately after a fresh code exchange" from a normal cold boot — that's
 * its own scoped follow-up, not something patched into this PR.
 *
 * Every network/storage/browser dependency is injectable via the `deps`
 * parameter (defaulted to the real browser/API behaviour) so this module is
 * unit-testable in `bun:test` without a DOM.
 */
import {
  clearStoredTokens,
  getStoredTokens,
  setStoredTokens,
  type StoredTokenPair,
  type TokenStorageAdapter,
} from './token_store.js';
import { currentRuntimeTarget, resolveApiUrl } from '../trpc.js';
import { resolveOAuthResource } from '../api_target.js';

export const CLIENT_ID = 'first_party_wagepro_web';
// Space-separated per RFC 6749 §3.3. `payroll:employer:read`/`:write` are
// the scopes PR2 (#22008) registered and PR3 (#22002) bound to
// WAGEPRO_OAUTH_AUDIENCE's restricted scope family. `payroll:self:read`
// (C7 PR B, #21938) is the ESS read scope this app's "My Pay" screen needs.
// `payroll:self:write` (adversarial-review finding B1) is required for the
// ESS leave submit/cancel procedures — without it every ESS write path 403s.
// See the module docblock's numbered rationale above.
// NOTE: adding `payroll:self:write` here requires the backend migration that
// adds it to `first_party_wagepro_web`'s registered scope ceiling (handled
// by the accompanying backend-agent migration).
export const OAUTH_SCOPE =
  'identity payroll:employer:read payroll:employer:write payroll:self:read payroll:self:write';
export const CALLBACK_PATH = '/auth/callback';

const PKCE_STATE_KEY = 'wagepro.auth.pkce_state.v1';
/** Refresh proactively this far ahead of actual access-token expiry. */
const REFRESH_MARGIN_MS = 60_000;
/** Bound how long `signOut()` waits on the (best-effort) revoke call. */
const REVOKE_TIMEOUT_MS = 3_000;

interface PendingPkceState {
  verifier: string;
  state: string;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

/**
 * A `fetch`-shaped function, deliberately narrower than `typeof fetch` (which
 * `@cloudflare/workers-types` augments with a `preconnect` method that a
 * plain test double would otherwise have to implement).
 */
export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

/**
 * Injectable dependencies for every exported function below. All are
 * optional — omitting one falls back to the real browser/API behaviour.
 * Tests always supply every field so no browser global (`window`, `fetch`)
 * is ever touched.
 */
export interface PkceDeps {
  tokenAdapter?: TokenStorageAdapter;
  sessionAdapter?: TokenStorageAdapter;
  fetchImpl?: FetchLike;
  now?: () => number;
  apiBaseUrl?: string;
  origin?: string;
  navigate?: (url: string) => void;
  /** Overrides {@link REVOKE_TIMEOUT_MS} — tests use this to avoid a real wait. */
  revokeTimeoutMs?: number;
  /**
   * Overrides the RFC 8707 `resource` indicator sent on the authorize
   * request. Defaults to `resolveOAuthResource(currentRuntimeTarget(),
   * origin)` — which is `origin` itself on the web, and the app's canonical
   * origin inside the Capacitor shell (whose served origin,
   * `http://localhost`, matches no configured audience slot). Separate from
   * `origin`, which still builds `redirect_uri`, precisely because those two
   * values diverge in the shell. See `lib/api_target.ts`'s module docblock.
   */
  resource?: string;
}

function browserSessionAdapter(): TokenStorageAdapter {
  return window.sessionStorage;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** RFC 7636 §4.1 code verifier — 32 random bytes, base64url-encoded. */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** RFC 7636 §4.2 code challenge — S256(verifier), base64url-encoded. */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/**
 * Validates an unknown JSON body against the {@link TokenResponse} shape
 * before it is trusted. Both `/oauth/token` call sites (authorization-code
 * exchange and refresh) deserialize across a network boundary — a
 * same-origin server bug or a misconfigured proxy returning an unexpected
 * 200 body must not silently propagate `undefined` fields into stored
 * tokens. Returns `null` (never throws) so each caller can fold this into
 * its own existing success/failure handling.
 */
function parseTokenResponse(raw: unknown): TokenResponse | null {
  const json = raw as Partial<TokenResponse> | null | undefined;
  if (
    !json ||
    typeof json.access_token !== 'string' ||
    typeof json.refresh_token !== 'string' ||
    typeof json.expires_in !== 'number'
  ) {
    return null;
  }
  return json as TokenResponse;
}

/**
 * Whether `url` is the OAuth callback landing on this app — a `code` param
 * on the exact `/auth/callback` path registered as this client's
 * `redirect_uri`. Deliberately does NOT go through `src/lib/router.ts` (the
 * in-app hash router) — this check runs before that router ever engages.
 */
export function isOAuthCallbackUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.pathname === CALLBACK_PATH && parsed.searchParams.has('code');
}

/**
 * The IdP's `error` query param (RFC 6749 §4.1.2.1 — e.g. `invalid_scope`,
 * `access_denied`) when the redirect back to `/auth/callback` represents a
 * rejection rather than a successful `code`. Checked independently of
 * {@link isOAuthCallbackUrl}, which requires `code` — an error-only redirect
 * would otherwise go undetected, and the boot sequence would fall through to
 * "no token -> unauthenticated", whose caller auto-redirects back to the IdP,
 * hitting the same error again: a silent infinite redirect loop.
 */
export function getOAuthCallbackError(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.pathname !== CALLBACK_PATH) return null;
  return parsed.searchParams.get('error');
}

/**
 * OIDC Core §3.1.2.1 `prompt` values this app sends. `['login']` asks the
 * authorization server to RE-AUTHENTICATE the user rather than answer the
 * request from a live session cookie.
 *
 * Passed on the authorize request that follows a DELIBERATE `signOut()`
 * (main.tsx's `SignedOutScreen`). `signOut()` revokes this app's own token
 * pair but never ends the Ledger session cookie — that cookie is host-scoped
 * and shared with every other first-party client, so WagePro signing out must
 * not log the Ledger dashboard out from under the user. Without this prompt,
 * pressing "Sign in" again resolved against whoever was still signed in:
 * either the consent screen for that user, or (with a matching consent row)
 * a silently re-minted token and no visible sign-in step at all. With it,
 * `GET /oauth/authorize` sends the user to Ledger's real sign-in form first
 * and returns them to this same authorization request afterwards
 * (`apps/api/src/routes/oauth.ts`, `forceLogin`).
 *
 * Note this is a re-authentication request, NOT a logout: no client can end a
 * session cookie it does not own, by design.
 */
export const REAUTHENTICATE_PROMPT: readonly string[] = ['login'];

/**
 * Non-dependency options for {@link beginSignIn} — deliberately a SECOND
 * parameter rather than a field on {@link PkceDeps}, which is strictly the
 * injectable-test-double surface (network, storage, clock, navigation).
 * `prompt` is a protocol parameter of the request itself, not a dependency
 * of it.
 */
export interface BeginSignInOptions {
  /**
   * OIDC `prompt` values to send, space-delimited per the spec. Omitted
   * entirely when empty, so the default call is byte-identical to the
   * pre-existing authorize URL.
   */
  prompt?: readonly string[];
  /**
   * Entity-id hint forwarded from the Ledger Apps launcher
   * (`?entity_id=<id>` on `/login?sso=ledger`). When present, appended as
   * `entity_id` on the `/oauth/authorize` request so the Ledger API can
   * pre-select the right organisation. Never placed in the PKCE `state`.
   * WagePro does not trust this value locally — the entity the app boots
   * into still comes from `auth.oauthGrant`.
   */
  entityId?: string;
}

/**
 * Starts the sign-in flow: generates a PKCE verifier/challenge + anti-CSRF
 * state, persists the verifier+state for the callback to consume, and
 * full-page-redirects to the authorization endpoint. Returns the
 * authorize-URL that was (or would be) navigated to, so callers/tests can
 * assert its shape without needing a real navigation to occur.
 */
export async function beginSignIn(
  deps: PkceDeps = {},
  options: BeginSignInOptions = {},
): Promise<string> {
  const sessionAdapter = deps.sessionAdapter ?? browserSessionAdapter();
  const apiBaseUrl = deps.apiBaseUrl ?? resolveApiUrl();
  const origin = deps.origin ?? window.location.origin;
  const navigate =
    deps.navigate ??
    ((url: string) => {
      window.location.href = url;
    });

  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateState();

  const pending: PendingPkceState = { verifier, state };
  sessionAdapter.setItem(PKCE_STATE_KEY, JSON.stringify(pending));

  const redirectUri = `${origin}${CALLBACK_PATH}`;
  const authorizeUrl = new URL('/oauth/authorize', apiBaseUrl);
  authorizeUrl.searchParams.set('client_id', CLIENT_ID);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('code_challenge', challenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');
  authorizeUrl.searchParams.set('redirect_uri', redirectUri);
  authorizeUrl.searchParams.set('scope', OAUTH_SCOPE);
  authorizeUrl.searchParams.set('state', state);
  // RFC 8707 resource indicator — this app's CANONICAL origin, so the API
  // mints a token whose audience matches env.WAGEPRO_OAUTH_AUDIENCE (#21929
  // PR3) instead of a null-audience identity-only token.
  //
  // On the web this resolves to `origin`, exactly as before — the browser
  // origin IS the app's identity there, and `redirectUri` above uses the same
  // value. Inside the Capacitor shell (apps/wagepro-mobile) the two DIVERGE
  // on purpose: `redirectUri` must stay the real WebView origin
  // (`http://localhost`, exact-string matched against the client's registered
  // redirect URIs), while `resource` must be the app's canonical origin,
  // because `http://localhost` matches no configured audience slot and would
  // mint a token every subsequent call rejects as FORBIDDEN — the silent
  // infinite-redirect failure this module's docblock records as #22019.
  // `lib/api_target.ts` owns that split and documents it in full.
  authorizeUrl.searchParams.set(
    'resource',
    deps.resource ?? resolveOAuthResource(currentRuntimeTarget(), origin),
  );
  // OIDC `prompt` — space-delimited value list per the spec (the API parses it
  // as a set, so a single value and a list are handled identically). Set only
  // when a caller asked for one: an empty/absent list must leave the param off
  // the URL entirely, not send `prompt=`, which the spec does not define.
  const promptValues = options.prompt ?? [];
  if (promptValues.length > 0) {
    authorizeUrl.searchParams.set('prompt', promptValues.join(' '));
  }
  if (options.entityId) {
    authorizeUrl.searchParams.set('entity_id', options.entityId);
  }

  const target = authorizeUrl.toString();
  navigate(target);
  return target;
}

// React StrictMode double-invokes effects, and the callback code is
// single-use server-side — a second exchange attempt for the same load would
// fail loudly. Dedupe on a module-level in-flight promise rather than per-code
// bookkeeping, since only one sign-in-callback flow is ever in progress at a
// time for a given page load.
let inFlightExchange: Promise<void> | null = null;

/**
 * Completes the sign-in flow from an OAuth callback URL: validates the
 * returned `state` against what `beginSignIn` persisted, exchanges the
 * authorization code for a token pair (PKCE `code_verifier` proof), stores
 * the tokens, and clears the in-flight PKCE state.
 *
 * Throws on any failure (missing code, state mismatch, exchange rejected) —
 * callers decide how to degrade (the auth boot sequence in
 * `auth_context.tsx` treats a thrown error as "stay unauthenticated").
 */
export async function completeSignIn(
  currentUrl: string,
  deps: PkceDeps = {},
): Promise<void> {
  if (inFlightExchange) return inFlightExchange;
  inFlightExchange = doCompleteSignIn(currentUrl, deps).finally(() => {
    inFlightExchange = null;
  });
  return inFlightExchange;
}

async function doCompleteSignIn(
  currentUrl: string,
  deps: PkceDeps,
): Promise<void> {
  const sessionAdapter = deps.sessionAdapter ?? browserSessionAdapter();
  const tokenAdapter = deps.tokenAdapter;
  const fetchImpl = deps.fetchImpl ?? fetch;
  const now = deps.now ?? Date.now;
  const apiBaseUrl = deps.apiBaseUrl ?? resolveApiUrl();
  const origin = deps.origin ?? window.location.origin;

  const url = new URL(currentUrl);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  if (!code) {
    throw new Error('OAuth callback missing authorization code');
  }

  const raw = sessionAdapter.getItem(PKCE_STATE_KEY);
  if (!raw) {
    throw new Error('No in-flight sign-in found for this callback');
  }
  let pending: PendingPkceState;
  try {
    pending = JSON.parse(raw) as PendingPkceState;
  } catch {
    throw new Error('Corrupt in-flight sign-in state');
  }
  if (!returnedState || returnedState !== pending.state) {
    throw new Error('OAuth state mismatch');
  }

  const redirectUri = `${origin}${CALLBACK_PATH}`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: CLIENT_ID,
    code_verifier: pending.verifier,
  });

  const response = await fetchImpl(`${apiBaseUrl}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(
      `OAuth authorization code exchange failed with status ${String(response.status)}`,
    );
  }

  let rawBody: unknown;
  try {
    rawBody = await response.json();
  } catch {
    // A 200 response whose body isn't valid JSON at all (e.g. an
    // intermediary — misconfigured proxy, captive portal — returning an
    // HTML page) is just as much a malformed response as valid JSON in the
    // wrong shape. Fold it into the same definitive-failure path rather than
    // letting a raw SyntaxError escape.
    throw new Error(
      'OAuth authorization code exchange returned a malformed response',
    );
  }
  const json = parseTokenResponse(rawBody);
  if (!json) {
    throw new Error(
      'OAuth authorization code exchange returned a malformed response',
    );
  }
  const tokens: StoredTokenPair = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: now() + json.expires_in * 1000,
  };
  await setStoredTokens(tokens, tokenAdapter);

  // Only clear the in-flight PKCE state once the exchange has actually
  // succeeded — a failed exchange leaves it in place so a stray duplicate
  // callback invocation still has something to compare `state` against
  // (it will fail the same way, not silently pass).
  sessionAdapter.removeItem(PKCE_STATE_KEY);
}

let inFlightRefresh: Promise<string | null> | null = null;

/**
 * Returns a currently-valid access token, refreshing it first if it is
 * within `REFRESH_MARGIN_MS` of expiry (or already expired). Returns `null`
 * if there is no stored token, or refresh definitively failed (dead refresh
 * token — storage is cleared in that case so the caller re-authenticates).
 *
 * A *transient* refresh failure (network error, 5xx) does NOT clear storage
 * — it returns the existing access token if it hasn't actually expired yet,
 * so a brief network blip never forces a re-login.
 */
export async function getValidToken(
  deps: PkceDeps = {},
): Promise<string | null> {
  const tokenAdapter = deps.tokenAdapter;
  const now = deps.now ?? Date.now;

  const stored = await getStoredTokens(tokenAdapter);
  if (!stored) return null;

  if (stored.expiresAt - now() > REFRESH_MARGIN_MS) {
    return stored.accessToken;
  }

  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = refreshStoredToken(stored, deps).finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function refreshStoredToken(
  stored: StoredTokenPair,
  deps: PkceDeps,
): Promise<string | null> {
  const tokenAdapter = deps.tokenAdapter;
  const fetchImpl = deps.fetchImpl ?? fetch;
  const now = deps.now ?? Date.now;
  const apiBaseUrl = deps.apiBaseUrl ?? resolveApiUrl();

  // Multiple tabs share the same localStorage pair but `inFlightRefresh`
  // dedupes only per-tab. If another tab already rotated the pair since we
  // read `stored`, firing our own refresh would replay an already-used
  // refresh token — the server's reuse detection treats that as theft and
  // revokes the ENTIRE token family, logging every tab out. Re-read storage
  // first: if someone beat us to it and the result is comfortably valid, use
  // it directly with no network call; otherwise refresh using whatever the
  // latest known-good refresh token is, never the one we started with.
  const current = (await getStoredTokens(tokenAdapter)) ?? stored;
  if (
    current.refreshToken !== stored.refreshToken &&
    current.expiresAt - now() > REFRESH_MARGIN_MS
  ) {
    return current.accessToken;
  }

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: current.refreshToken,
    client_id: CLIENT_ID,
  });

  let response: Response;
  try {
    response = await fetchImpl(`${apiBaseUrl}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    // Network failure: preserve the existing pair. We only got here because
    // we're inside the refresh margin, not necessarily past real expiry.
    return current.expiresAt > now() ? current.accessToken : null;
  }

  if (response.status === 400 || response.status === 401) {
    // Definitive rejection (invalid_grant: reuse/expired, or invalid_client)
    // — the refresh token is dead. Clear it so the caller re-authenticates
    // instead of retrying a token that will never succeed.
    await clearStoredTokens(tokenAdapter);
    return null;
  }

  if (!response.ok) {
    // Non-definitive server error (5xx, rate limit) — same fail-preserve
    // behaviour as a network error; do not clear a possibly-still-good token.
    return current.expiresAt > now() ? current.accessToken : null;
  }

  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    // A non-JSON 200 body here is much more likely an intermediary
    // (captive portal, misconfigured proxy) intercepting the response than
    // our own IdP being broken — unlike the wrong-shape-but-valid-JSON case
    // below, this is NOT treated as a definitive protocol bug. Fail-preserve
    // the same as a network error / 5xx: never clear a user's session over
    // hotel wifi.
    return current.expiresAt > now() ? current.accessToken : null;
  }
  const json = parseTokenResponse(raw);
  if (!json) {
    // The server returned 200 with a body that doesn't match the expected
    // token-response shape — a definitive protocol-level problem, not a
    // transient network blip. Clear the pair rather than retrying it, same
    // as the 400/401 branch above.
    await clearStoredTokens(tokenAdapter);
    return null;
  }
  const next: StoredTokenPair = {
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: now() + json.expires_in * 1000,
  };
  await setStoredTokens(next, tokenAdapter);
  return next.accessToken;
}

/**
 * Best-effort server-side token revocation, then an unconditional local
 * sign-out. Revocation is bounded by `REVOKE_TIMEOUT_MS` and never throws —
 * a slow or failed revoke call must not hang (or block) sign-out.
 */
export async function signOut(deps: PkceDeps = {}): Promise<void> {
  const tokenAdapter = deps.tokenAdapter;
  const fetchImpl = deps.fetchImpl ?? fetch;
  const apiBaseUrl = deps.apiBaseUrl ?? resolveApiUrl();
  const revokeTimeoutMs = deps.revokeTimeoutMs ?? REVOKE_TIMEOUT_MS;

  const stored = await getStoredTokens(tokenAdapter);
  if (stored) {
    const body = new URLSearchParams({
      token: stored.refreshToken,
      client_id: CLIENT_ID,
    });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), revokeTimeoutMs);
    try {
      await fetchImpl(`${apiBaseUrl}/oauth/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        signal: controller.signal,
      });
    } catch {
      // Best-effort — proceed to local sign-out regardless.
    } finally {
      clearTimeout(timeoutId);
    }
  }

  await clearStoredTokens(tokenAdapter);
}
